import React, { useEffect, useRef, useState } from "react";
import { useContent } from "../context/ContentContext";
import { ImageEditable, readImageFile } from "./SectionControls";
import { Plus, X, ChevronLeft, ChevronRight, Image as ImageIcon, Upload, Timer } from "lucide-react";

export const MAX_HERO_PHOTOS = 5;
export const DEFAULT_ROTATE_SEC = 5;
const SPEED_OPTIONS = [3, 5, 8, 10, 15];

export function heroImages(section) {
  const list = section.images && section.images.length ? section.images : [section.imageUrl];
  return list.filter(Boolean);
}

export default function HeroCarousel({ section }) {
  const { updateSection, editMode, isAdmin } = useContent();
  const editing = editMode && isAdmin;
  const images = heroImages(section);
  const [active, setActive] = useState(0);
  const fileRef = useRef(null);
  const idx = Math.min(active, images.length - 1);
  const rotateSec = Number(section.rotateSec) || DEFAULT_ROTATE_SEC;

  useEffect(() => {
    if (editing || images.length < 2) return;
    const t = setInterval(() => setActive((a) => (a + 1) % images.length), rotateSec * 1000);
    return () => clearInterval(t);
  }, [editing, images.length, rotateSec]);

  const save = (next) =>
    updateSection(section.id, { images: next, imageUrl: next[0] || "" });

  const replace = (i, url) => save(images.map((img, k) => (k === i ? url : img)));
  const add = (url) => {
    if (!url || images.length >= MAX_HERO_PHOTOS) return;
    save([...images, url]);
    setActive(images.length);
  };
  const remove = (i) => {
    if (images.length <= 1) return;
    save(images.filter((_, k) => k !== i));
    setActive(Math.max(0, Math.min(idx, images.length - 2)));
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const next = [...images];
    [next[i], next[j]] = [next[j], next[i]];
    save(next);
    setActive(j);
  };

  return (
    <div data-testid="hero-carousel" className="relative h-[340px] w-full overflow-hidden md:h-[520px]">
      {images.map((src, i) =>
        editing && i === idx ? (
          <ImageEditable
            key={`edit-${i}`}
            src={src}
            alt={`Hero photo ${i + 1}`}
            onChange={(url) => replace(i, url)}
            className="h-[340px] w-full object-cover object-center md:h-[520px]"
          />
        ) : (
          <img
            key={i}
            src={src}
            alt={`Hero photo ${i + 1}`}
            data-testid={`hero-slide-${i}`}
            data-active={i === idx}
            className="absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-1000 ease-in-out"
            style={{ opacity: i === idx ? 1 : 0, pointerEvents: editing ? "none" : "auto" }}
          />
        )
      )}

      {!editing && images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2" data-testid="hero-dots">
          {images.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-dot-${i}`}
              aria-label={`Show photo ${i + 1}`}
              onClick={() => setActive(i)}
              className={`h-2.5 rounded-full transition-all duration-300 ${
                i === idx ? "w-7 bg-white" : "w-2.5 bg-white/55 hover:bg-white/80"
              }`}
            />
          ))}
        </div>
      )}

      {editing && (
        <div
          data-testid="hero-photo-manager"
          className="absolute inset-x-0 bottom-0 z-20 flex items-center gap-3 overflow-x-auto bg-slate-900/85 px-4 py-3 backdrop-blur"
        >
          <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-300">
            Photos {images.length}/{MAX_HERO_PHOTOS}
          </span>
          {images.map((src, i) => (
            <div key={i} className="group/thumb relative shrink-0">
              <button
                data-testid={`hero-thumb-${i}`}
                onClick={() => setActive(i)}
                className={`block h-14 w-24 overflow-hidden rounded border-2 transition-colors ${
                  i === idx ? "border-white" : "border-transparent opacity-70 hover:opacity-100"
                }`}
              >
                <img src={src} alt="" className="h-full w-full object-cover" />
              </button>
              <div className="absolute -top-2 -right-2 flex gap-0.5 opacity-0 transition-opacity group-hover/thumb:opacity-100">
                <button
                  data-testid={`hero-thumb-left-${i}`}
                  title="Move earlier"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  className="rounded-full bg-white p-0.5 text-slate-700 shadow disabled:opacity-30"
                >
                  <ChevronLeft size={12} />
                </button>
                <button
                  data-testid={`hero-thumb-right-${i}`}
                  title="Move later"
                  onClick={() => move(i, 1)}
                  disabled={i === images.length - 1}
                  className="rounded-full bg-white p-0.5 text-slate-700 shadow disabled:opacity-30"
                >
                  <ChevronRight size={12} />
                </button>
                {images.length > 1 && (
                  <button
                    data-testid={`hero-thumb-remove-${i}`}
                    title="Remove photo"
                    onClick={() => remove(i)}
                    className="rounded-full bg-red-600 p-0.5 text-white shadow"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
          {images.length < MAX_HERO_PHOTOS && (
            <div className="flex shrink-0 gap-1.5">
              <button
                data-testid="hero-add-url-btn"
                onClick={() => {
                  const url = window.prompt("Photo URL:", "https://");
                  if (url && url !== "https://") add(url);
                }}
                className="flex h-14 flex-col items-center justify-center gap-0.5 rounded border border-dashed border-slate-400 px-3 text-[11px] font-semibold text-slate-200 hover:bg-white/10"
              >
                <ImageIcon size={14} /> Add URL
              </button>
              <button
                data-testid="hero-add-upload-btn"
                onClick={() => fileRef.current?.click()}
                className="flex h-14 flex-col items-center justify-center gap-0.5 rounded border border-dashed border-slate-400 px-3 text-[11px] font-semibold text-slate-200 hover:bg-white/10"
              >
                <Upload size={14} /> Upload
              </button>
              <input
                ref={fileRef}
                data-testid="hero-add-file-input"
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []).slice(0, MAX_HERO_PHOTOS - images.length);
                  const urls = await Promise.all(files.map(readImageFile));
                  if (urls.length) save([...images, ...urls]);
                  e.target.value = "";
                }}
              />
            </div>
          )}
          {images.length >= MAX_HERO_PHOTOS && (
            <span className="shrink-0 text-xs text-slate-400" data-testid="hero-limit-note">
              <Plus size={12} className="inline" /> Limit reached — remove a photo to add another
            </span>
          )}
          <label className="ml-auto flex shrink-0 items-center gap-2 text-xs font-semibold text-slate-200">
            <Timer size={14} />
            <span>Each photo</span>
            <select
              data-testid="hero-rotate-select"
              value={rotateSec}
              onChange={(e) => updateSection(section.id, { rotateSec: Number(e.target.value) })}
              className="rounded border border-slate-500 bg-slate-800 px-2 py-1 text-xs text-white outline-none focus:border-white"
            >
              {SPEED_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s} sec
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
    </div>
  );
}
